import { OutboundScanType } from '@truepill/tpos-types'
import type { MutationSendOutboundScanArgs } from 'gql/generated/graphql'
import type { PackingAdditonalData, Task } from 'providers/TaskProvider'
import type { OTCProduct, PackingMaterialItem, RXFillRequest } from 'types'
import { getScannedTasksForShippingLabel } from './print'
import { getOverriddenItems, getSerialNumbersByOtcProduct, shouldCompleteTask } from './tasksUtils'

type ApplicationIdentifier = {
  length: number // Length of the AI
  variableLength?: boolean // Set to true if the AI has variable length
}

// For list of Application Identifiers:
// https://www.gs1.org/standards/barcodes/application-identifiers/01?lang=en
const aiDefinitions: { [key: string]: ApplicationIdentifier } = {
  '00': { length: 18 },
  '01': { length: 14 }, // GTIN
  '10': { length: 20, variableLength: true }, // Batch or lot number
  '17': { length: 6 }, // Expiry date
  '21': { length: 20, variableLength: true }, // Serial number
  '91': { length: 100, variableLength: true }, // Company internal information
  '92': { length: 100, variableLength: true }, // Company internal information
  '93': { length: 100, variableLength: true }, // Company internal information
  '94': { length: 100, variableLength: true }, // Company internal information
  '95': { length: 100, variableLength: true }, // Company internal information
  '96': { length: 100, variableLength: true }, // Company internal information
  '97': { length: 100, variableLength: true }, // Company internal information
  '98': { length: 100, variableLength: true }, // Company internal information
  '99': { length: 100, variableLength: true }, // Company internal information
}

export function parseGS1String(gs1String: string): Record<string, string> {
  const result: Record<string, string> = {}
  let index = 0

  while (index < gs1String.length) {
    let aiFound = false

    // Try to match known AIs
    for (const ai in aiDefinitions) {
      if (gs1String.startsWith(ai, index)) {
        const { length, variableLength } = aiDefinitions[ai]
        let valueEnd = index + ai.length + length

        if (variableLength) {
          // For variable length AIs, find the next GS or the end of the string
          const gsIndex = gs1String.indexOf(String.fromCharCode(29), index + ai.length)
          valueEnd = gsIndex !== -1 ? gsIndex : gs1String.length
        }

        const value = gs1String.substring(index + ai.length, valueEnd)
        result[ai] = value
        index = valueEnd + (variableLength ? 1 : 0) // Skip the GS if variable length
        aiFound = true
        break
      }
    }

    if (!aiFound) {
      index++
    }
  }

  return result
}

export function shortCodeScan(
  value: string,
  rxFillRequests: RXFillRequest[],
  tasks: Task[],
  completeTask: (key: string) => void,
  showInfoToast: (message: string) => void,
  showSuccessToast: (message: string) => void,
): void {
  const rxFillRequest = rxFillRequests.find(rxFillRequest => rxFillRequest.fill.shortFillCode === value)
  if (rxFillRequest) {
    const fillTask = tasks.find(({ key }) => key === value)
    if (!fillTask) {
      showInfoToast('Navigating to ' + value + '.')
      return
    }
    completeTask(rxFillRequest.fill.shortFillCode)
    showSuccessToast('Scanned RX barcode')
  }
}

export function SKUScan(
  value: string,
  tasks: Task[],
  completeTask: (key: string) => void,
  showSuccessToast: (message: string) => void,
  showErrorToast: (message: string) => void,
): void {
  try {
    const task = tasks.find(({ code, completed }) => value === code && !completed)
    if (!task) {
      return
    }
    showSuccessToast('Scanned product: ' + value)
    if (shouldCompleteTask(true, task.additionalData)) {
      completeTask(task.key)
    }
  } catch (e) {
    showErrorToast(e.message)
    console.error(e)
  }
}

export function UPCScan(
  value: string,
  tasks: Task[],
  updateTask: (key: string, updates: Partial<Task>) => void,
  completeTask: (key: string) => void,
  showSuccessToast: (message: string) => void,
  showErrorToast: (message: string) => void,
): void {
  try {
    if (!value) {
      return
    }

    const task = tasks.find(
      ({ additionalData, completed }) => additionalData?.upcOptions?.includes(value) && completed === false,
    )
    if (!task) {
      showErrorToast('Incorrect product/stock (UPC) barcode scanned.')
      return
    }
    showSuccessToast('Scanned product: ' + task.label + ', UPC:' + value)
    const updatedAdditionalData: PackingAdditonalData = {
      ...(task.additionalData || {}),
      upcOptions: task.additionalData?.upcOptions || [],
      upcScanned: value,
    }
    updateTask(task.key, { additionalData: updatedAdditionalData })

    if (shouldCompleteTask(true, updatedAdditionalData)) {
      completeTask(task.key)
    }
  } catch (e) {
    showErrorToast(e.message)
  }
}

// handles GS1 -> UPC and Serial Number (if required)
export function GS1Scan(
  value: string,
  tasks: Task[],
  updateTask: (key: string, updates: Partial<Task>) => void,
  completeTask: (key: string) => void,
  showSuccessToast: (message: string) => void,
  showErrorToast: (message: string) => void,
): void {
  const parsedGS1 = parseGS1String(value)
  console.debug(`GS1 barcode scanned, GTIN ${parsedGS1['01']} Serial number ${parsedGS1['21']}`)
  const upcParsed = parsedGS1['01']
  if (!upcParsed) {
    return
  }

  // Find task by checking if any of its UPC options are contained in the parsed UPC
  const task = tasks.find(
    ({ additionalData, completed }) =>
      additionalData?.upcOptions?.some(upcOption => upcParsed.includes(upcOption)) && completed === false,
  )
  if (!task) {
    showErrorToast('Incorrect product/stock (UPC) barcode scanned.')
    return
  }

  const matchedUpc = task.additionalData?.upcOptions?.find(upcOption => upcParsed.includes(upcOption))

  let successToastMessage = `Scanned product: ${task.label}, UPC: ${matchedUpc}`

  const serialNumber = parsedGS1['21']

  const scannedSerialNumbersForProduct = tasks
    .filter(t => t.label === task.label && t.additionalData?.scannedSerialNumber)
    .map(t => t.additionalData?.scannedSerialNumber)

  if (scannedSerialNumbersForProduct.includes(serialNumber)) {
    showErrorToast(
      `Serial number ${serialNumber} for product ${task.label} has already been captured. Please scan the correct item.`,
    )
    return
  }

  const updatedAdditionalData: PackingAdditonalData = {
    ...(task.additionalData || {}),
    upcOptions: task.additionalData?.upcOptions || [],
    upcScanned: matchedUpc,
  }

  if (
    serialNumber &&
    updatedAdditionalData.isSerialized &&
    !updatedAdditionalData.scannedSerialNumber &&
    !updatedAdditionalData.manualSerialNumber
  ) {
    successToastMessage += `, Serial number: ${serialNumber}`
    updatedAdditionalData.scannedSerialNumber = serialNumber
  }
  showSuccessToast(successToastMessage)
  updateTask(task.key, { additionalData: updatedAdditionalData })

  if (updatedAdditionalData.upcScanned && shouldCompleteTask(true, updatedAdditionalData)) {
    completeTask(task.key)
  }
}

export function shippingLabelScan(
  value: string,
  trackingNumber: string | undefined,
  tasks: Task[],
  otcProducts: OTCProduct[],
  packingMaterials: PackingMaterialItem[] | undefined,
  coreOrderId: string,
  orderStatus: string,
  isLabelPrinted: boolean,
  sendOutboundScan: (options: { variables: MutationSendOutboundScanArgs; refetchQueries: string[] }) => void,
  showSuccessToast: (message: string) => void,
  showErrorToast: (message: string) => void,
  acquireOutboundScanLock?: () => boolean,
): void {
  if (!trackingNumber) {
    showErrorToast('No shipping label available to scan.')
    return
  }

  // the scanned value could contain the tracking number
  if (value !== trackingNumber && !value.includes(trackingNumber)) {
    showErrorToast(`Invalid shipping label ${value} scanned.`)
    return
  }

  // lock to avoid duplicate scans
  if (acquireOutboundScanLock && !acquireOutboundScanLock()) {
    return
  }

  showSuccessToast('Scanned shipping label: ' + value)

  sendOutboundScan({
    variables: {
      trackingNumber,
      outboundScanType: OutboundScanType.fulfillment,
      ...(!isLabelPrinted && {
        serialNumbersByOtcProduct: getSerialNumbersByOtcProduct(tasks, otcProducts),
        overrideItems: getOverriddenItems(tasks, otcProducts, packingMaterials),
        scannedTasks: getScannedTasksForShippingLabel(orderStatus, tasks),
      }),
      coreOrderId: Number(coreOrderId),
    },
    refetchQueries: ['getFullOrder'],
  })
}
